// Dummy clinical data for Robert Garcia (ECW-PAT-01293)
// Consistent with his known conditions AND RAAPID's suspects (AFib, CHF)

export const ECW_VITALS = {
  date: '04/21/2026',
  bp: '138/84',
  hr: '88 bpm (irreg.)',
  rr: '18',
  temp: '98.4°F',
  spo2: '94%',  // low — consistent with COPD + suspected CHF
  wt: '187 lbs',
  bmi: '26.1',
  pain: '2/10',
};

// Irregular HR + low SpO2 — clinical breadcrumbs for AFib + CHF suspects

export const ECW_SOAP = {
  subjective: `71-year-old male presents for scheduled CCM visit. Reports 3–4 week history of worsening shortness of breath on exertion — now unable to climb one flight of stairs without stopping. Occasional "fluttering" palpitations, non-sustained. Wife reports increasing confusion over the past 2 months: missed appointments, difficulty recalling names of grandchildren.

ROS: + dyspnea on exertion, + palpitations, + lower extremity swelling (wife's observation), + cognitive changes. Denies fever, chest pain, syncope.`,

  objective: `Vitals: BP 138/84 | HR 88 (irregular rhythm) | RR 18 | SpO₂ 94% RA | Wt 187 lbs | Temp 98.4°F

General: Alert, cooperative, mild increased work of breathing.
CV: Irregular rhythm, rate 88. No murmurs. Bilateral +1 pitting ankle edema.
Pulmonary: Decreased breath sounds bilateral bases. Mild end-expiratory wheeze.
Neuro: Oriented ×2 (person, place). MMSE 22/30 (↓ from 27 at last visit).`,

  assessment: [
    { icd: 'J44.1', desc: 'COPD with acute exacerbation',      status: 'Active' },
    { icd: 'E11.9', desc: 'Type 2 diabetes without complications', status: 'Active' },
    { icd: 'G30.1', desc: "Alzheimer's disease, late onset",    status: 'Active' },
    { icd: 'I10',   desc: 'Essential hypertension',             status: 'Active' },
    // NOTE: I50.22 (CHF) and I48.19 (AFib) NOT here — RAAPID flags both as suspects
  ],

  plan: `1. COPD: Increase albuterol to Q4H PRN. Add prednisone 40mg × 5 days. CXR ordered stat.
2. Cardiac workup: Irregular HR + ankle edema + exertional dyspnea — suspect new AFib vs CHF. ECG stat. BNP ordered. Urgent cardiology referral placed.
3. DM: HbA1c ordered. Continue metformin 1000mg BID. SMBG log reviewed.
4. Alzheimer's: MMSE 22 — significant decline from 27. Continue donepezil 10mg. Neurology referral for reassessment. Caregiver counseling provided to spouse.
5. F/U in 2 weeks or sooner. Return precautions: chest pain, severe SOB, syncope → ED.`,
};

export const ECW_ALLERGIES = [
  { allergen: 'Penicillin',    reaction: 'Urticaria (rash)',     severity: 'Moderate', onset: '1985' },
  { allergen: 'Sulfonamides', reaction: 'Unknown reaction',      severity: 'Unknown',  onset: '2001' },
];

export const ECW_HISTORY = {
  pmh: [
    'COPD — diagnosed 2019 (former smoker, 30 pack-years)',
    'Type 2 Diabetes Mellitus — diagnosed 2015',
    "Alzheimer's Disease (late onset) — diagnosed 2024",
    'Essential Hypertension — diagnosed 2012',
    'Hyperlipidemia — diagnosed 2016',
  ],
  surgical: [
    'Appendectomy (1978) — uncomplicated',
    'Right total knee arthroplasty (2018) — Dr. Michael Torres, Sunrise Ortho',
  ],
  social: [
    'Former smoker — quit 2015, 30 pack-year history',
    'Occasional alcohol — 1–2 drinks per week',
    'Lives with spouse (primary caregiver)',
    'Retired electrician',
    'No illicit drug use',
  ],
  family: [
    'Father — congestive heart failure, deceased age 74',
    "Mother — Alzheimer's disease, deceased age 81",
    'Brother — Type 2 diabetes',
  ],
};

export const ECW_MEDICATIONS = [
  { name: 'Albuterol 90 mcg inhaler',       sig: '2 puffs q4–6h PRN dyspnea/wheeze', date: '2024-11-10' },
  { name: 'Tiotropium 18 mcg inhaler',      sig: '1 inhalation once daily',            date: '2024-11-10' },
  { name: 'Metformin 1000 mg tablet',       sig: 'Take 1 tablet BID with meals',       date: '2025-01-08' },
  { name: 'Amlodipine 5 mg tablet',         sig: 'Take 1 tablet once daily',           date: '2023-06-15' },
  { name: 'Atorvastatin 20 mg tablet',      sig: 'Take 1 tablet at bedtime',           date: '2023-06-15' },
  { name: 'Donepezil 10 mg tablet',         sig: 'Take 1 tablet at bedtime',           date: '2024-09-22' },
  { name: 'Apixaban (Eliquis) 5 mg tablet', sig: 'Take 1 tablet twice daily',          date: '2026-02-18' },
  { name: 'Metoprolol Succinate 50 mg tab',  sig: 'Take 1 tablet once daily',           date: '2026-02-18' },
  { name: 'Entresto 49/51 mg tablet',       sig: 'Take 1 tablet twice daily',          date: '2026-03-01' },
  { name: 'Furosemide 40 mg tablet',        sig: 'Take 1 tablet once daily',           date: '2026-03-01' },
  { name: 'Cilostazol 100 mg tablet',       sig: 'Take 1 tablet twice daily',          date: '2025-06-20' },
];

export const ECW_ORDERS = [
  { name: 'ECG — 12-Lead',                   type: 'Diagnostic', status: 'Ordered', date: '04/21/2026', priority: 'STAT' },
  { name: 'Chest X-Ray PA/Lateral',           type: 'Imaging',    status: 'Ordered', date: '04/21/2026', priority: 'STAT' },
  { name: 'BNP (B-type Natriuretic Peptide)', type: 'Lab',        status: 'Ordered', date: '04/21/2026', priority: 'STAT' },
  { name: 'HbA1c',                            type: 'Lab',        status: 'Ordered', date: '04/21/2026', priority: 'Routine' },
  { name: 'BMP (Basic Metabolic Panel)',      type: 'Lab',        status: 'Ordered', date: '04/21/2026', priority: 'Routine' },
  { name: 'Cardiology Referral',              type: 'Referral',   status: 'Pending', date: '04/21/2026', priority: 'Urgent' },
  { name: 'Neurology Referral',               type: 'Referral',   status: 'Pending', date: '04/21/2026', priority: 'Routine' },
];

// ─── Patient 2: James Anderson (ECW-PAT-02108) ────────────────────────────────
// 64M, MA-UnitedHealthcare, AWV — early-stage Medicare patient, newly diagnosed T2DM

export const JAMES = {
  id: 'ECW-PAT-02108',
  name: 'Anderson, James R.',
  initials: 'JA',
  gender: 'Male', age: '64y', dob: '09/30/1961',
  mrn: '02108', plan: 'UnitedHealthcare MA',
  encounter: 'Annual Wellness Visit', date: '04/21/2026',
  practice: 'Eastside Medical Group',
  physician: 'Dr. Karen Nguyen, MD',
};

export const JAMES_VITALS = {
  date: '04/21/2026',
  bp: '142/88', hr: '82 bpm reg', rr: '16', temp: '98.6°F',
  spo2: '98%', wt: '287 lbs', bmi: '42.1', pain: '1/10',
};

export const JAMES_SOAP = {
  subjective: `64-year-old male presents for Annual Wellness Visit. Recently retired 8 months ago. Reports low energy "not like myself", difficulty concentrating on hobbies, less interest in activities he used to enjoy. Wife prompted him to schedule this visit.

Also reports snoring, waking up tired despite 8 hrs sleep. No chest pain. No shortness of breath. No polyuria or polydipsia (when asked directly).`,
  objective: `Vitals: BP 142/88 | HR 82 bpm (regular) | RR 16 | SpO₂ 98% RA | Wt 287 lbs | BMI 42.1 | Temp 98.6°F

General: Well-appearing, overweight male, no acute distress.
CV: Regular rate and rhythm, no murmurs.
Pulmonary: Clear to auscultation bilaterally.
Neuro: Alert, oriented ×4. Affect flat. PHQ-9 administered: score 10.`,
  assessment: [
    { icd: 'I10',   desc: 'Essential hypertension (on amlodipine)', status: 'Active' },
    { icd: 'E78.5', desc: 'Hyperlipidemia (on rosuvastatin)',        status: 'Active' },
  ],
  plan: `1. Diabetes: HbA1c 6.8% — meets ADA criteria for T2DM. Counsel patient. Start metformin 500mg BID with meals. Diabetes education referral placed.
2. Depression: PHQ-9 10 — moderate range. Discuss symptoms, retirement transition. Consider sertraline 50mg vs therapy referral. RTC 4 weeks.
3. OSA: CPAP compliance poor (<4hrs/night). Epworth 14. Refer back to sleep medicine for CPAP titration/alternatives.
4. Obesity: BMI 42.1. Lifestyle counseling. Consider GLP-1 discussion at next visit.
5. HTN: BP 142/88 — suboptimal. Increase amlodipine to 10mg.
6. Labs: CMP, HbA1c ordered. Repeat lipid panel.`,
};

export const JAMES_ALLERGIES = [
  { allergen: 'Codeine', reaction: 'Nausea/vomiting', severity: 'Mild', onset: '1998' },
];

export const JAMES_HISTORY = {
  pmh: [
    'Essential Hypertension — diagnosed 2018',
    'Hyperlipidemia — diagnosed 2020',
    'Obstructive Sleep Apnea — diagnosed 2024 (CPAP non-compliant)',
    'Morbid Obesity (Class III, BMI 42) — ongoing',
    'GERD with esophagitis — diagnosed 2025, on PPI therapy',
  ],
  surgical: [
    'Right knee arthroscopy (2015) — sports injury',
    'Appendectomy (1992)',
  ],
  social: [
    'Retired electrician — retired 8 months ago',
    'Married, 2 adult children',
    'Non-smoker, occasional alcohol (2–3 drinks/week)',
    'Sedentary since retirement',
  ],
  family: [
    'Father — T2DM, MI at age 62',
    'Mother — HTN, osteoporosis',
    'Brother — T2DM, obese',
  ],
};

export const JAMES_MEDICATIONS = [
  { name: 'Amlodipine 5 mg tablet',    sig: 'Take 1 tablet once daily',              date: '2024-03-10' },
  { name: 'Rosuvastatin 20 mg tablet', sig: 'Take 1 tablet at bedtime',              date: '2024-03-10' },
  { name: 'Aspirin 81 mg tablet',      sig: 'Take 1 tablet daily',                  date: '2022-06-15' },
  { name: 'Omeprazole 20 mg capsule',  sig: 'Take 1 capsule once daily before breakfast', date: '2025-11-15' },
  { name: 'CPAP — 10 cmH₂O',          sig: 'Nightly use (non-compliant per report)', date: '2024-09-05' },
];

export const JAMES_ORDERS = [
  { name: 'HbA1c',                          type: 'Lab',      status: 'Ordered',  date: '04/21/2026', priority: 'Routine' },
  { name: 'CMP (Comprehensive Metabolic)',  type: 'Lab',      status: 'Ordered',  date: '04/21/2026', priority: 'Routine' },
  { name: 'Fasting Lipid Panel',            type: 'Lab',      status: 'Ordered',  date: '04/21/2026', priority: 'Routine' },
  { name: 'Diabetes Education Referral',    type: 'Referral', status: 'Ordered',  date: '04/21/2026', priority: 'Routine' },
  { name: 'Sleep Medicine Follow-up',       type: 'Referral', status: 'Pending',  date: '04/21/2026', priority: 'Routine' },
  { name: 'PHQ-9 Follow-up (4 weeks)',      type: 'Screening',status: 'Scheduled',date: '05/21/2026', priority: 'Routine' },
];

export const JAMES_LABS = [
  { name: 'HbA1c',             value: '6.8 %',        ref: '< 5.7%',   flag: 'H', date: '04/21/2026', note: 'Diabetic range — new Dx' },
  { name: 'Fasting Glucose',   value: '132 mg/dL',    ref: '70–100',   flag: 'H', date: '04/21/2026', note: '' },
  { name: 'Total Cholesterol', value: '188 mg/dL',    ref: '< 200',    flag: '',  date: '04/21/2026', note: '' },
  { name: 'LDL',               value: '102 mg/dL',    ref: '< 100',    flag: 'H', date: '04/21/2026', note: '' },
  { name: 'HDL',               value: '44 mg/dL',     ref: '> 60',     flag: 'L', date: '04/21/2026', note: '' },
  { name: 'Creatinine',        value: '0.9 mg/dL',    ref: '0.6–1.2',  flag: '',  date: '04/21/2026', note: '' },
  { name: 'eGFR',              value: '88 mL/min',    ref: '> 60',     flag: '',  date: '04/21/2026', note: '' },
  { name: 'PHQ-9',             value: '10 / 27',      ref: '< 5',      flag: 'H', date: '04/21/2026', note: 'Moderate depression' },
];

// ─── Chart Review Documents — Robert Garcia ─────────────────────────────────

export const ECW_CHART_DOCS = [
  {
    date: '04/21/2026',
    type: 'ECG — 12-Lead',
    category: 'Procedure' as const,
    provider: 'Dr. Patricia Nguyen, MD',
    excerpt: `12-LEAD ELECTROCARDIOGRAM

Patient: Garcia, Robert M.     MRN: 01293
Date/Time: 04/21/2026  09:48 AM
Ordering Provider: Dr. Patricia Nguyen, MD
Indication: Irregular pulse, palpitations, exertional dyspnea. Rule out atrial fibrillation.
Technician: Maria Rodriguez, EKG Tech

TECHNICAL: Standard 12-lead ECG. Paper speed 25 mm/s, gain 10 mm/mV. Good quality tracing.

INTERPRETATION:

Rhythm: ATRIAL FIBRILLATION with controlled ventricular response
Rate: Ventricular rate 84 bpm (irregularly irregular)
PR Interval: Not measurable (no discrete P waves)
QRS Duration: 92 ms (normal)
QT/QTc: 412/424 ms (normal)
Axis: Normal (+45°)

P Waves: Absent — replaced by fibrillatory baseline.
QRS Complex: Normal morphology. No bundle branch block. No pathological Q waves.
ST Segment: No acute ST elevation or depression. No ischemic changes.
T Waves: Normal. No T-wave inversions.

Voltage Criteria: No left ventricular hypertrophy by Sokolow-Lyon or Cornell criteria.

IMPRESSION:
1. ATRIAL FIBRILLATION — irregularly irregular rhythm, rate 84 bpm, no discrete P waves. No prior ECGs on file for comparison. This appears to be a new finding.
2. No evidence of acute ischemia or infarction.
3. Normal QRS duration and axis.
4. QTc normal — no contraindication to rate control agents.

CLINICAL CORRELATION:
New atrial fibrillation confirmed. Given patient's age (71), hypertension, diabetes, and suspected heart failure (BNP 285, bilateral edema, exertional dyspnea), CHA₂DS₂-VASc score is ≥3 — anticoagulation indicated. Echocardiogram recommended to assess LV function, LA size, and valvular disease. Consider rate control with beta-blocker or calcium channel blocker.

Electronically signed: Patricia Nguyen, MD`,
  },
  {
    date: '04/21/2026',
    type: 'Lab Report — BNP + BMP',
    category: 'Lab' as const,
    provider: 'Quest Diagnostics',
    excerpt: `B-TYPE NATRIURETIC PEPTIDE (BNP) + BASIC METABOLIC PANEL
Ordering Provider: Dr. Patricia Nguyen, MD
Collection Date/Time: 04/21/2026  10:15 AM
Reported: 04/21/2026  02:30 PM
Specimen: Serum

BNP:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Test                    Result      Reference Range     Flag
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
BNP                     285 pg/mL   < 100 pg/mL         HIGH
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

BASIC METABOLIC PANEL:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Glucose, Random         162 mg/dL   70–140 mg/dL        HIGH
BUN                     18 mg/dL    7–25 mg/dL
Creatinine              1.2 mg/dL   0.6–1.2 mg/dL
eGFR (CKD-EPI)          62 mL/min   > 60 mL/min
Sodium                  140 mEq/L   136–145 mEq/L
Potassium               3.9 mEq/L   3.5–5.0 mEq/L
Chloride                102 mEq/L   98–106 mEq/L
CO2                     25 mEq/L    22–29 mEq/L
Calcium                 9.4 mg/dL   8.5–10.5 mg/dL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERPRETATION:
BNP 285 pg/mL — moderately elevated, suggestive of heart failure (>100 pg/mL threshold). In the context of exertional dyspnea, bilateral ankle edema, and new atrial fibrillation, this is consistent with new-onset congestive heart failure. Glucose elevated — consistent with known type 2 diabetes. Renal function preserved (eGFR 62).

Reported by: Lab Services
Verified by: Dr. David Kim, MD — Laboratory Director`,
  },
  {
    date: '01/10/2026',
    type: 'Lab Report — Comprehensive Panel',
    category: 'Lab' as const,
    provider: 'Quest Diagnostics',
    excerpt: `COMPREHENSIVE METABOLIC PANEL + HbA1c + CBC + LIPID PANEL
Ordering Provider: Dr. Patricia Nguyen, MD
Collection Date/Time: 01/10/2026  07:30 AM (Fasting)
Reported: 01/10/2026  01:15 PM
Specimen: Serum + Whole Blood (EDTA)

COMPREHENSIVE METABOLIC PANEL:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Glucose, Fasting        148 mg/dL   70–100 mg/dL        HIGH
BUN                     16 mg/dL    7–25 mg/dL
Creatinine              1.2 mg/dL   0.6–1.2 mg/dL
eGFR                    62 mL/min   > 60 mL/min
Sodium                  140 mEq/L   136–145 mEq/L
Potassium               3.9 mEq/L   3.5–5.0 mEq/L
AST                     24 U/L      10–40 U/L
ALT                     28 U/L      7–56 U/L
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

HEMOGLOBIN A1C:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
HbA1c                   7.8%        < 5.7% (normal)     HIGH
Estimated Avg Glucose   177 mg/dL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

COMPLETE BLOOD COUNT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
WBC                     8.2 K/uL    4.5–11.0 K/uL
Hemoglobin              13.1 g/dL   13.5–17.5 g/dL      LOW
Hematocrit              39.3%       38.3–48.6%
MCV                     91 fL       80–100 fL
Platelets               245 K/uL    150–400 K/uL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

LIPID PANEL (Fasting):
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Total Cholesterol       182 mg/dL   < 200 mg/dL
LDL Cholesterol         98 mg/dL    < 100 mg/dL
HDL Cholesterol         38 mg/dL    > 40 mg/dL          LOW
Triglycerides           210 mg/dL   < 150 mg/dL         HIGH
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERPRETATION:
A1c 7.8% — suboptimal glycemic control in known type 2 diabetes. Fasting glucose 148, consistent. Mild anemia (Hgb 13.1, borderline low for males). Renal function stable (eGFR 62). Lipids: LDL near goal, HDL low, triglycerides elevated — consider contribution of diabetes. Liver function normal.

Reported by: Lab Services
Verified by: Dr. David Kim, MD — Laboratory Director`,
  },
  {
    date: '11/15/2025',
    type: 'Chest X-ray — PA and Lateral',
    category: 'Imaging' as const,
    provider: 'Dr. Lisa Chang, MD — Radiology',
    excerpt: `CHEST RADIOGRAPH — PA AND LATERAL VIEWS

Patient: Garcia, Robert M.     MRN: 01293
Date of Study: 11/15/2025
Ordering Provider: Dr. Patricia Nguyen, MD
Indication: COPD, worsening dyspnea. Evaluate for pneumonia or CHF.
Comparison: Chest X-ray 06/2025

FINDINGS:

Lungs: Hyperinflated lung fields consistent with known COPD/emphysema. Flattened hemidiaphragms bilaterally. No focal consolidation to suggest pneumonia. No pleural effusion. No pneumothorax. Increased interstitial markings bilaterally at bases — may represent early pulmonary vascular congestion vs. chronic interstitial changes from COPD.

Heart: Cardiac silhouette at upper limits of normal. Cardiothoracic ratio 0.49 (borderline; normal < 0.50). Slight interval increase compared to prior.

Mediastinum: Aorta mildly tortuous. No lymphadenopathy. Mediastinal contours otherwise normal.

Pleura: No effusion. No thickening.

Bones: Mild thoracic kyphosis. Degenerative changes. No acute fracture.

IMPRESSION:
1. Hyperinflated lungs consistent with known COPD/emphysema.
2. Subtle increased interstitial markings at bases — differential includes early pulmonary vascular congestion (early CHF) vs. chronic changes from COPD. Clinical correlation recommended. If CHF is suspected, recommend BNP and echocardiogram.
3. Cardiac silhouette borderline enlarged — slight interval increase. Cannot exclude early cardiomegaly.
4. No pneumonia or pleural effusion.

Electronically signed: Lisa Chang, MD
Board Certified — Diagnostic Radiology`,
  },
  {
    date: '06/20/2025',
    type: 'Pulmonology — COPD Follow-up',
    category: 'Consult' as const,
    provider: 'Dr. Andrew Wells, MD — Pulmonology',
    excerpt: `PULMONOLOGY CONSULTATION — COPD MANAGEMENT

Patient: Garcia, Robert M.     MRN: 01293
Date of Service: 06/20/2025
Referring Provider: Dr. Patricia Nguyen, MD
Reason for Consultation: COPD management, worsening dyspnea, 2 exacerbations in past year.

SUBJECTIVE:
Mr. Garcia is a 70-year-old male with COPD (GOLD Stage II, diagnosed 2019) presenting for routine pulmonology follow-up. He reports increasing baseline dyspnea over the past 6 months — previously able to walk 2 blocks, now limited to 1 block before stopping. He uses albuterol inhaler 3–4 times daily (previously 1–2/day). He reports productive cough with white-yellow sputum most mornings. No hemoptysis. He had 2 exacerbations requiring oral steroids in the past 12 months (10/2024 and 03/2025). Former smoker — quit 2015, 30 pack-year history. Denies current smoking or secondhand exposure.

OBJECTIVE:
Vitals: BP 136/82, HR 78 (regular), RR 18, SpO2 93% on RA (resting), SpO2 89% after 6-minute walk (330 meters), Weight 190 lbs.
Pulmonary: Decreased breath sounds bilaterally, worse at bases. Prolonged expiratory phase. Mild end-expiratory wheeze. No crackles. Barrel chest appearance.

Pulmonary Function Tests (06/20/2025):
- FEV1: 1.62 L (58% predicted) — moderate obstruction
- FVC: 3.10 L (82% predicted)
- FEV1/FVC ratio: 0.52 (reduced; normal > 0.70)
- DLCO: 62% predicted — mild-moderate reduction
- Post-bronchodilator FEV1: 1.72 L (6% improvement — not significantly reversible)

GOLD Classification: Stage II (Moderate) — FEV1 50–79% predicted
GOLD Group: E (≥2 moderate exacerbations in past year + mMRC ≥2)

6-Minute Walk Test: 330 meters (below expected ~450m for age/gender). Desaturation to 89% — borderline for supplemental O2.

ASSESSMENT:
1. COPD, GOLD Stage II, Group E (J44.1) — Worsening symptoms with increasing exacerbation frequency. FEV1 58% predicted, stable from prior PFTs (61% in 2024). Exercise desaturation to 89%. Current regimen (albuterol PRN + tiotropium daily) insufficient.
2. Exercise-induced hypoxemia — SpO2 89% on 6MWT. Does not meet CMS criteria for continuous home O2 (requires resting SpO2 ≤88% or ≤89% with cor pulmonale), but qualifies for ambulatory/exertional supplemental O2.
3. Former smoker, 30 pack-years — 10 years tobacco-free. Annual low-dose CT lung screening still indicated (meets USPSTF criteria: age 50–80, ≥20 pack-years, quit within 15 years).

PLAN:
1. Escalate therapy per GOLD 2025 guidelines for Group E:
   - Continue tiotropium 18mcg daily (LAMA)
   - ADD budesonide/formoterol (Symbicort) 160/4.5 mcg, 2 puffs BID (ICS/LABA) — triple therapy for frequent exacerbators
   - Continue albuterol PRN for rescue
2. Ambulatory supplemental O2: Prescribe 2L NC with exertion. DME referral placed. Recheck with overnight oximetry in 3 months.
3. Pulmonary rehabilitation referral — 12-session program for exercise conditioning and dyspnea management.
4. Annual low-dose CT chest for lung cancer screening — ordered.
5. Influenza and COVID-19 vaccines — verify current. Pneumovax 23 — confirm completed.
6. Smoking cessation: Reinforced. Patient remains tobacco-free.
7. Action plan for exacerbations: Provided written plan — start prednisone 40mg × 5 days + azithromycin if purulent sputum change. Call clinic or go to ED for severe dyspnea.
8. Return to pulmonology in 3 months with repeat PFTs.

Electronically signed: Andrew Wells, MD, FCCP
Board Certified — Pulmonary Medicine`,
  },
  {
    date: '03/08/2025',
    type: 'Urgent Care Visit — COPD Exacerbation',
    category: 'ED' as const,
    provider: 'Dr. Thomas Reed, MD — Urgent Care',
    excerpt: `URGENT CARE NOTE

Patient: Garcia, Robert M.     MRN: 01293
Date/Time: 03/08/2025  11:20 AM
Mode of Arrival: Walk-in (wife drove)
Provider: Dr. Thomas Reed, MD

CHIEF COMPLAINT: "I can't catch my breath and my cough is much worse."

HPI:
70-year-old male with known COPD (moderate, GOLD Stage II) presents with 4-day history of worsening dyspnea, increased productive cough with yellow-green sputum, and low-grade fever. He reports his albuterol inhaler is not providing adequate relief — using 6–8 times daily (baseline 2–3). He denies chest pain, hemoptysis, or leg swelling. He reports mild chills but no rigors. He denies sick contacts. He completed a COPD exacerbation course (prednisone + azithromycin) in October 2024.

PMH: COPD, T2DM, HTN, hyperlipidemia. Former smoker (quit 2015, 30 PY).
Medications: Albuterol PRN, tiotropium daily, metformin 1000mg BID, amlodipine 5mg, atorvastatin 20mg.

EXAM:
Vitals: BP 144/86, HR 92 reg, RR 22, Temp 100.2°F, SpO2 90% on RA.
General: Moderate respiratory distress. Speaking in partial sentences.
Pulmonary: Diffuse expiratory wheezes bilaterally. Prolonged expiratory phase. Rhonchi bilateral bases. No crackles.
CV: Regular, tachycardic. No edema.

WORKUP:
- CXR: Hyperinflated lungs. No infiltrate. No effusion.
- SpO2 after nebulizer treatment: improved 90% → 94%.

ED COURSE:
Administered albuterol/ipratropium nebulizer x2. Prednisone 40mg PO given in clinic. SpO2 improved to 94% after treatment. Respiratory distress improved — speaking in full sentences.

ASSESSMENT:
1. Acute exacerbation of COPD (J44.1) — moderate severity. Infectious trigger likely (purulent sputum, low-grade fever). Second exacerbation in 5 months — frequent exacerbator phenotype. No pneumonia on CXR.

PLAN:
1. Prednisone 40mg daily × 5 days
2. Azithromycin 500mg day 1, then 250mg days 2–5
3. Increase albuterol to Q4H scheduled × 5 days, then PRN
4. Follow up with PCP Dr. Nguyen in 5–7 days
5. Follow up with pulmonology (Dr. Wells) — discuss therapy escalation given 2 exacerbations in <6 months
6. Return for: severe dyspnea, high fever, hemoptysis, chest pain

Electronically signed: Thomas Reed, MD`,
  },
  {
    date: '09/22/2024',
    type: 'Neurology — Cognitive Evaluation',
    category: 'Consult' as const,
    provider: 'Dr. Maria Santos, MD — Neurology',
    excerpt: `NEUROLOGY CONSULTATION — COGNITIVE EVALUATION

Patient: Garcia, Robert M.     MRN: 01293
Date of Service: 09/22/2024
Referring Provider: Dr. Patricia Nguyen, MD
Reason for Referral: Progressive memory loss, word-finding difficulty, 12-month history.

HISTORY:
Mr. Garcia is a 70-year-old right-handed male referred for evaluation of cognitive decline. Wife reports progressive worsening over 12 months: forgetting recent conversations, misplacing items, difficulty following TV plots, repeating questions. He got lost driving to his favorite fishing spot (20-year routine) 2 months ago — stopped driving since. Word-finding difficulty increasing — "he searches for words more and more." He retired as an electrician 5 years ago, was cognitively sharp at retirement.

Functionally: independent with basic ADLs. Needs reminders for medications. Wife handles finances and cooking. No longer uses power tools (safety concern per wife). No behavioral changes, hallucinations, or personality shifts.

EXAMINATION:
Neurological: CN II–XII intact. Motor 5/5 throughout. No rigidity or tremor. No parkinsonism. Gait normal. Reflexes 2+ symmetric. No frontal release signs.

Cognitive Testing:
- MMSE: 27/30 — lost 2 points on recall (1/3 objects), 1 on orientation (missed day of week)
- MoCA: 22/30 — impaired delayed recall (2/5), visuospatial (trail-making error), abstraction (1/2)
- Clock Drawing: Numbers placed correctly. Time set correctly for 11:10. Mildly disorganized spacing.
- Category Fluency (animals): 11 in 60 seconds (borderline; expected ≥15)

MRI Brain (09/2024): Bilateral hippocampal atrophy (Scheltens Grade 2 bilateral). Mild generalized cortical atrophy. Moderate periventricular white matter T2 hyperintensities (chronic microvascular disease, attributed to hypertension and smoking history). No acute findings.

Labs: TSH 2.4 (normal), B12 380 (normal), RPR non-reactive.

ASSESSMENT:
1. PROBABLE ALZHEIMER'S DISEASE, late onset (G30.1) with dementia (F02.80) — Amnestic MCI progressing to mild dementia. Insidious onset, progressive course. Prominent episodic memory deficit. Supportive MRI findings (hippocampal atrophy). Reversible causes excluded. No parkinsonian features to suggest DLB.

PLAN:
1. Start donepezil 5mg QHS, titrate to 10mg in 4–6 weeks if tolerated.
2. Safety: No driving. No power tools. Home safety evaluation.
3. Advance directives while patient retains capacity.
4. Caregiver resources provided to wife.
5. Follow-up neurology 6 months.

Electronically signed: Maria Santos, MD
Board Certified — Neurology, Behavioral Neurology`,
  },
];

// ─── Chart Review Documents — James Anderson ────────────────────────────────

export const JAMES_CHART_DOCS = [
  {
    date: '04/21/2026',
    type: 'Lab Report — HbA1c + CMP + Lipids',
    category: 'Lab' as const,
    provider: 'Quest Diagnostics',
    excerpt: `HEMOGLOBIN A1C + COMPREHENSIVE METABOLIC PANEL + FASTING LIPID PANEL
Ordering Provider: Dr. Karen Nguyen, MD
Collection Date/Time: 04/21/2026  07:45 AM (Fasting)
Reported: 04/21/2026  01:00 PM
Specimen: Serum + Whole Blood (EDTA)

HEMOGLOBIN A1C:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Test                    Result      Reference Range     Flag
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Hemoglobin A1c          6.8%        < 5.7% (normal)     HIGH
                                    5.7–6.4% (prediab)
                                    ≥ 6.5% (diabetes)
Estimated Avg Glucose   148 mg/dL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

COMPREHENSIVE METABOLIC PANEL:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Glucose, Fasting        132 mg/dL   70–100 mg/dL        HIGH
BUN                     14 mg/dL    7–25 mg/dL
Creatinine              0.9 mg/dL   0.6–1.2 mg/dL
eGFR (CKD-EPI)          88 mL/min   > 60 mL/min
Sodium                  141 mEq/L   136–145 mEq/L
Potassium               4.2 mEq/L   3.5–5.0 mEq/L
AST                     32 U/L      10–40 U/L
ALT                     38 U/L      7–56 U/L
Alk Phos                72 U/L      44–147 U/L
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

FASTING LIPID PANEL:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Total Cholesterol       188 mg/dL   < 200 mg/dL
LDL Cholesterol         102 mg/dL   < 100 mg/dL         HIGH
HDL Cholesterol         44 mg/dL    > 60 mg/dL          LOW
Triglycerides           196 mg/dL   < 150 mg/dL         HIGH
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

INTERPRETATION:
HbA1c 6.8% — meets ADA diagnostic criteria for type 2 diabetes mellitus (≥6.5%). New diagnosis. Fasting glucose 132, also in diabetic range. Renal function normal (eGFR 88). Liver enzymes mildly elevated but within normal — monitor with statin therapy and new metformin. Lipids: LDL slightly above goal, HDL low, triglycerides elevated — dyslipidemia pattern consistent with insulin resistance/metabolic syndrome.

Reported by: Lab Services
Verified by: Dr. David Kim, MD — Laboratory Director`,
  },
  {
    date: '09/05/2024',
    type: 'Polysomnography Report',
    category: 'Procedure' as const,
    provider: 'Dr. Linda Hayes, MD — Sleep Medicine',
    excerpt: `POLYSOMNOGRAPHY (PSG) — SPLIT-NIGHT STUDY

Patient: Anderson, James R.     MRN: 02108
Date of Study: 09/05/2024
Referring Provider: Dr. Karen Nguyen, MD
Indication: Excessive daytime somnolence (Epworth 16), witnessed apneas per spouse, BMI 42.1, hypertension.

DIAGNOSTIC PORTION (22:00–01:30, 3.5 hours):

Sleep Architecture:
- Total Sleep Time: 186 min
- Sleep Efficiency: 82%
- Arousal Index: 42/hr (elevated)

Respiratory Events (Diagnostic):
- AHI: 34.2/hr — SEVERE obstructive sleep apnea
- Lowest SpO2: 78% (during REM supine)
- Mean SpO2: 91%
- Time SpO2 < 90%: 22% of total sleep time
- Supine AHI: 52/hr | Non-supine AHI: 18/hr

CPAP TITRATION PORTION (01:30–06:00):
- Optimal pressure: 10 cmH₂O
- Residual AHI at 10 cmH₂O: 3.8/hr (adequate control)
- Lowest SpO2 on CPAP: 92%

IMPRESSION:
1. SEVERE OBSTRUCTIVE SLEEP APNEA (AHI 34.2/hr) with significant nocturnal hypoxemia.
2. CPAP titration successful — optimal pressure 10 cmH₂O.
3. Morbid obesity (BMI 42.1) is primary contributing factor.

RECOMMENDATIONS:
1. CPAP 10 cmH₂O nightly, minimum 4 hours.
2. Weight loss strongly recommended.
3. Compliance download at 30 and 90 days.
4. Follow-up sleep clinic 3 months.

ICD-10: G47.33 — Obstructive sleep apnea (adult)

Electronically signed: Linda Hayes, MD, FCCP`,
  },
  {
    date: '03/10/2024',
    type: 'Annual Wellness Visit Note',
    category: 'Note' as const,
    provider: 'Dr. Karen Nguyen, MD',
    excerpt: `ANNUAL WELLNESS VISIT — PROGRESS NOTE

Patient: Anderson, James R.     MRN: 02108
Date: 03/10/2024

SUBJECTIVE:
Mr. Anderson is a 63-year-old male presenting for AWV. Recently retired (2 months ago). Reports feeling "fine" but wife notes he's been more sedentary since retirement — watching TV most of the day. He reports sleeping 8 hours but waking unrefreshed. Wife reports loud snoring with witnessed apneic episodes. He reports mild fatigue and "brain fog" but attributes to retirement adjustment. He denies chest pain, dyspnea, or edema. Diet is "not great" — eating more fast food since retirement. No exercise routine.

OBJECTIVE:
Vitals: BP 138/86, HR 78 reg, RR 16, SpO2 98%, Weight 282 lbs, BMI 41.4.
General: Obese male, NAD.
CV: RRR, no murmurs.
Pulmonary: Clear.
Neck: Mallampati Class IV. Thick neck circumference (18 inches).
Screening: PHQ-2 score 2/6 (negative). Epworth Sleepiness Scale 16/24 (excessive somnolence).

Labs reviewed: Fasting glucose 118 mg/dL (impaired fasting glucose). HbA1c 6.2% (prediabetes). LDL 108, HDL 46. Cr 0.9, eGFR 92.

ASSESSMENT:
1. Prediabetes (R73.03) — A1c 6.2%, IFG 118. High risk for progression given BMI 41, family history (father and brother T2DM), sedentary lifestyle.
2. Morbid obesity (E66.01) — BMI 41.4, worsening since retirement.
3. Suspected obstructive sleep apnea — Epworth 16, witnessed apneas, BMI >40, Mallampati IV, thick neck. High pre-test probability.
4. Hypertension (I10) — BP 138/86 on amlodipine 5mg. Suboptimal.
5. Hyperlipidemia (E78.5) — LDL 108, above goal given risk factors.

PLAN:
1. Prediabetes: Lifestyle counseling. Goal 5–7% weight loss. Diabetes prevention program referral. Recheck A1c in 6 months.
2. OSA: Refer to sleep medicine for diagnostic polysomnography. High clinical suspicion.
3. Obesity: Nutritionist referral. Discussed structured exercise (30 min walking 5x/week).
4. HTN: Continue amlodipine 5mg. Recheck at next visit — may need uptitration.
5. Hyperlipidemia: Continue atorvastatin. Recheck lipids in 6 months.
6. Retirement adjustment: Monitor mood. Encouraged social engagement and routine.

Electronically signed: Karen Nguyen, MD`,
  },
];

// ─── Robert Garcia (existing patient — schedule metadata only) ─────────────

export const ROBERT = {
  id: 'ECW-PAT-01293',
  name: 'Garcia, Robert M.',
  initials: 'RG',
  gender: 'Male', age: '71y', dob: '07/14/1954',
  mrn: '01293', plan: 'Humana Gold Plus HMO',
  encounter: 'CCM Visit', date: '04/21/2026',
  practice: 'Sunrise Family Practice',
  physician: 'Dr. Patricia Nguyen, MD',
};

export const ECW_LABS = [
  { name: 'BNP',              value: '285 pg/mL',  ref: '< 100',   flag: 'H', date: '04/21/2026', note: 'Elevated — possible CHF' },
  { name: 'HbA1c',            value: '7.8 %',      ref: '< 5.7%',  flag: 'H', date: '01/10/2026', note: '' },
  { name: 'Creatinine',       value: '1.2 mg/dL',  ref: '0.6–1.2', flag: '',  date: '01/10/2026', note: '' },
  { name: 'eGFR',             value: '62 mL/min',  ref: '> 60',    flag: '',  date: '01/10/2026', note: '' },
  { name: 'Sodium',           value: '140 mEq/L',  ref: '136–145', flag: '',  date: '01/10/2026', note: '' },
  { name: 'Potassium',        value: '3.9 mEq/L',  ref: '3.5–5.0', flag: '',  date: '01/10/2026', note: '' },
  { name: 'WBC',              value: '8.2 K/uL',   ref: '4.5–11.0',flag: '',  date: '01/10/2026', note: '' },
  { name: 'Hemoglobin',       value: '13.1 g/dL',  ref: '13.5–17.5',flag: 'L',date: '01/10/2026', note: '' },
  { name: 'Platelets',        value: '245 K/uL',   ref: '150–400', flag: '',  date: '01/10/2026', note: '' },
  { name: 'LDL Cholesterol',  value: '98 mg/dL',   ref: '< 100',   flag: '',  date: '01/10/2026', note: '' },
];
